<template>
  <div class="entry-history">
    <div class="history-header">
      <div class="header-left">
        <h3>{{ t('excel.history.title', { descricao: props.descricao }) }}</h3>
        <el-radio-group v-model="timeGrouping" size="small" class="time-group">
          <el-radio-button value="detailed">{{ t('excel.history.detailed') }}</el-radio-button>
          <el-radio-button value="monthly">{{ t('excel.history.monthly') }}</el-radio-button>
          <el-radio-button value="yearly">{{ t('excel.history.yearly') }}</el-radio-button>
        </el-radio-group>
      </div>
      <el-button
        v-if="!showChart"
        type="text"
        @click="showChart = true"
        class="chart-toggle"
      >
        <el-icon><TrendCharts /></el-icon>
      </el-button>
    </div>

    <v-chart
      v-if="showChart"
      class="chart"
      :option="chartOption"
      autoresize
    />

    <el-table
      :data="displayedEntries"
      style="width: 100%"
      size="small"
      :stripe="true"
      :border="true"
      class="history-table"
    >
      <el-table-column 
        v-if="timeGrouping === 'detailed'"
        prop="id" 
        :label="t('excel.fields.id')" 
        width="120"
      >
        <template #default="{ row }">
          <span class="id-value">{{ row.id }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="dt" :label="t('excel.fields.date')" width="150">
        <template #default="{ row }">
          {{ formatDate(row.dt) }}
        </template>
      </el-table-column>
      <el-table-column prop="valor" :label="t('excel.fields.value')" width="150" align="right">
        <template #default="{ row }">
          {{ new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(row.valor) }}
        </template>
      </el-table-column>
      <el-table-column 
        v-if="timeGrouping === 'detailed'"
        prop="lastchanged" 
        :label="t('excel.fields.lastModified')" 
        min-width="200"
      >
        <template #default="{ row }">
          <span class="last-changed">{{ new Date(row.lastchanged).toLocaleString() }}</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { ExcelEntryDTO } from '../types/accounting'
import axios from 'axios'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import { TrendCharts } from '@element-plus/icons-vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

// Register ECharts components
use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent
])

const props = withDefaults(defineProps<{
  descricao: string
  showChart?: boolean
}>(), {
  showChart: true
})

const { t } = useI18n()
const authStore = useAuthStore()
const showChart = ref(props.showChart)

const entries = ref<ExcelEntryDTO[]>([])
const error = ref<string | null>(null)

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + authStore.keycloak?.token
  }
})

const timeGrouping = ref<'detailed' | 'monthly' | 'yearly'>('detailed')


const groupEntriesByTime = (entries: ExcelEntryDTO[]) => {
  if (timeGrouping.value === 'detailed') {
    return entries
  }

  const grouped = new Map<string, { dt: string; valor: number; count: number }>()
  
  entries.forEach(entry => {
    
    const date = new Date(entry.dt)
    const key = timeGrouping.value === 'monthly' 
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      : `${date.getFullYear()}`

    if (!grouped.has(key)) {
      grouped.set(key, { 
        dt: timeGrouping.value === 'monthly' 
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`
          : `${date.getFullYear()}-01-01`,
        valor: 0,
        count: 0
      })
    }
    
    let group = grouped.get(key);
    if (group) {
      group.valor = Number(group.valor) + Number(entry.valor);
      group.count++
      grouped.set(key, group);
    } else {
      console.error('Group not found for key:', key)
    }
  })

  return Array.from(grouped.values()).map(group => ({
    valor: group.valor, 
    dt: group.dt,
    descricao: props.descricao,
    lastchanged: undefined
  }))
}

const displayedEntries = computed(() => {
  const grouped = groupEntriesByTime(entries.value)
  return grouped.sort((a, b) => new Date(a.dt).getTime() - new Date(b.dt).getTime())
})

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  switch (timeGrouping.value) {
    case 'yearly':
      return date.getFullYear().toString()
    case 'monthly':
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    default:
      return date.toLocaleDateString()
  }
}

const chartOption = computed(() => ({
  title: {
    text: t('excel.history.chartTitle', { timeGrouping: t(`excel.history.${timeGrouping.value}`) }),
    left: 'center'
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params: any) => {
      const data = params[0].data
      return `${timeGrouping.value === 'yearly' 
        ? new Date(data[0]).getFullYear()
        : timeGrouping.value === 'monthly'
          ? new Date(data[0]).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
          : new Date(data[0]).toLocaleDateString()
      }<br/>
      ${t('excel.fields.value')}: ${new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(data[1])}`
    }
  },
  grid: {
    left: '5%',
    right: '5%',
    bottom: '10%',
    containLabel: true
  },
  xAxis: {
    type: 'time',
    boundaryGap: false,
    axisLabel: {
      formatter: (value: string) => new Date(value).toLocaleDateString()
    }
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: (value: number) => new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    }
  },
  dataZoom: [
    {
      type: 'inside',
      start: 0,
      end: 100
    }
  ],
  series: [
    {
      name: t('excel.fields.value'),
      type: 'bar',
      data: displayedEntries.value.map(entry => [entry.dt, entry.valor]),
      smooth: true,
      showSymbol: true,
      symbolSize: 8,
      lineStyle: {
        width: 3
      }
    }
  ]
}))

const fetchEntries = async (descricao: string) => {
  if (!descricao.trim()) {
    entries.value = []
    return
  }

  try {
    const response = await api.post<ExcelEntryDTO[]>(
      `${import.meta.env.VITE_EXCEL_API_URL}/excel/findByDescricao`, 
      { descricao: descricao }
    )
    entries.value = response.data
  } catch (err: any) {
    console.error('Failed to fetch entries by description:', err)
    error.value = err.response?.data?.message || 'Failed to fetch entries'
  }
}

watch(() => props.descricao, (newDescricao) => {
  fetchEntries(newDescricao)
}, { immediate: true })
</script>

<style scoped>
.entry-history {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.time-group {
  margin-left: 1rem;
}

.chart {
  height: 400px;
  margin-bottom: 2rem;
}

.history-table {
  margin-top: 1rem;
}

.id-value {
  font-family: monospace;
  font-size: 1.1em;
}

.last-changed {
  font-size: 0.9em;
  color: #606266;
}

h3 {
  margin: 0;
  color: #303133;
}

.chart-toggle {
  font-size: 1.2em;
}

:deep(.el-icon) {
  font-size: 1.2em;
}
</style> 